import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";

import { urls } from "@shared/constants";
import { useUtmParams } from "@shared/hooks";
import { appendUtmParams, getUtmParams, TUtmParams } from "@shared/tracking";
import {
  findEnCallPage,
  isExternalUrl,
  splitUrl,
  toWebUrl,
} from "@shared/utils";

import {
  TCall,
  TCallInfo,
  TCallPage,
  TSubmissionStatus,
} from "__generated__/types";

const UTM_MEDIUM = "call_grid";

/**
 * utm_medium describes the channel the visit came through, so it's only ours to set when
 * the visit didn't bring one. Links to a call page leave it out entirely — that page sets
 * its own medium, and stamping ours here would override it (see useSubmissionCTAButton).
 */
function withSurfaceMedium(utmParams: TUtmParams): TUtmParams {
  return { ...utmParams, utm_medium: utmParams.utm_medium ?? UTM_MEDIUM };
}

type TCallCardLinkCall = Pick<
  TCall,
  | "id"
  | "submissionEndDate"
  | "submissionStartDate"
  | "website"
  | "slug"
  | "useNewSubmissionWizard"
> & {
  callInfo?: Pick<TCallInfo, "id">;
  callPages?: Array<Pick<TCallPage, "slug" | "language">>;
};

export function getCallCardLink(
  call: TCallCardLinkCall,
  submissionStatus?: TSubmissionStatus,
  utmParams: TUtmParams = getUtmParams(),
): string | null {
  // QuickInfo renders the status-appropriate CTA itself (see useSubmissionCTAButton)
  if (call.callInfo) {
    return `?quickInfo=${call.id}`;
  }

  const callPage = findEnCallPage(call.callPages);
  if (callPage) {
    return appendUtmParams(urls.callPage(callPage.slug), utmParams);
  }

  const isOpen = Date.now() < new Date(call.submissionEndDate).valueOf();
  const startSubmission = () =>
    appendUtmParams(
      call.useNewSubmissionWizard
        ? urls.startSimplifiedSubmission(call.slug)
        : urls.startSubmission(call.id),
      withSurfaceMedium(utmParams),
    );

  // No quickInfo and no callPage: route by submission status, matching
  // useSubmissionStatus/useSubmissionCTAButton — a closed call keeps SUBMITTED
  // reachable but collapses DRAFT/IN_EDIT to CLOSED (no submission link).
  if (submissionStatus === TSubmissionStatus.Submitted) {
    return appendUtmParams(
      urls.viewSubmission(call.id),
      withSurfaceMedium(utmParams),
    );
  }
  if (isOpen && submissionStatus === TSubmissionStatus.InEdit) {
    return appendUtmParams(
      urls.finishSubmission(call.id),
      withSurfaceMedium(utmParams),
    );
  }
  if (isOpen && submissionStatus === TSubmissionStatus.Draft) {
    return startSubmission();
  }

  // No (reachable) submission. An external host gets fresh default params only — the visit's
  // campaign params must not leak off our own hosts. One of our own hosts gets them, but no
  // medium of ours: the website may well be the call's own call page, which sets its own.
  //
  // A website we can't follow — no scheme, or one the browser shouldn't follow from an href —
  // is treated as no website at all (see toWebUrl).
  const website = call.website ? toWebUrl(call.website) : null;
  if (website) {
    return appendUtmParams(
      website,
      isExternalUrl(website)
        ? { utm_source: "picter", utm_medium: UTM_MEDIUM }
        : utmParams,
    );
  }

  if (isOpen) {
    return startSubmission();
  }
  return null;
}

/**
 * Merge a path-relative link (`?quickInfo=…`) into the current path. Next would otherwise
 * resolve it against `router.pathname` alone, dropping everything the visitor arrived with —
 * campaign params included — and on a dynamic route that pathname is still the pattern, so
 * the href would point at a literal `/archive/[page]`.
 *
 * `keepCurrentQuery` separates the prerendered link from the hydrated one: the path of
 * `asPath` is part of the page's cache key and safe to bake into shared html, its query is not
 * (see `useCallCardLink`).
 */
function mergeIntoCurrentQuery(
  asPath: string,
  relativeLink: string,
  keepCurrentQuery: boolean,
) {
  const { baseUrl, query: currentQuery, hash } = splitUrl(asPath);
  const query = new URLSearchParams(keepCurrentQuery ? currentQuery : "");
  new URLSearchParams(relativeLink).forEach((value, key) =>
    query.set(key, value),
  );
  const fragment = keepCurrentQuery && hash ? `#${hash}` : "";
  return `${baseUrl}?${query}${fragment}`;
}

/**
 * `getCallCardLink` bound to the current visit: its UTM parameters, and — for the links that
 * are relative to the page we're on — its query.
 *
 * Both are known only on the client. The grid pages are statically rendered and ISR-cached
 * under a key that ignores the query string, so baking the visitor's query into the html would
 * serve it to everyone else, and react doesn't patch a mismatched `href` while hydrating. The
 * link therefore carries the path alone until this page has mounted, and gains the visitor's
 * query in a re-render react does apply.
 *
 * The cost is a narrow one: a card clicked in the moment before hydration navigates without
 * the campaign params. Leaking a stranger's campaign to every cached visitor is worse.
 */
export function useCallCardLink() {
  const { asPath } = useRouter();
  const utmParams = useUtmParams();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return useCallback(
    (call: TCallCardLinkCall, submissionStatus?: TSubmissionStatus) => {
      const link = getCallCardLink(call, submissionStatus, utmParams);
      return link?.startsWith("?")
        ? mergeIntoCurrentQuery(asPath, link, mounted)
        : link;
    },
    [utmParams, asPath, mounted],
  );
}
